import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import { DocDefaultPreferenceEnum } from "../Models/SharePointPracticeModel";

export class dmsHelperCommon {
    private fileImageCache: { [key: string]: string } = {};
    readonly FORBIDDEN_FILENAME_CHARS_PATTERN = '["*:<>?/\\\\|#]';
    readonly FORBIDDEN_FILENAME_CHARS_REGEX = new RegExp(this.FORBIDDEN_FILENAME_CHARS_PATTERN, 'g');
    readonly FORBIDDEN_FILENAME_CHARS_REGEX_TEST = new RegExp(this.FORBIDDEN_FILENAME_CHARS_PATTERN);
    readonly MSG_FORBIDDEN_FILENAME_CHARS_PATTERN = `Avoid using these characters: " * : <> ? / \ | #`;
    readonly ALLOWED_FOLDER_NAME_REGEX = /^[a-zA-Z0-9\s\-_]*$/;
    readonly FOLDER_NAME_ERROR_MSG = `Use only letters, numbers, spaces, '-' and '_'`;
    readonly RESERVED_FOLDER_NAMES_PATTERN = ['permanent',
    'temporary',
    'CON',
    'PRN',
    'AUX',
    'NUL',
    'COM1',
    'COM2',
    'COM3',
    'COM4',
    'COM5',
    'COM6',
    'COM7',
    'COM8',
    'COM9',
    'LPT1',
    'LPT2',
    'LPT3',
    'LPT4',
    'LPT5',
    'LPT6',
    'LPT7',
    'LPT8',
    'LPT9'];
    readonly MSG_RESERVED_FOLDER_NAMES_PATTERN = `This name is reserved and cannot be used.`;
    readonly MSG_RESERVED_FILE_NAMES_PATTERN = `This name is reserved and cannot be used.`;

    // File reserved names (excludes 'permanent' and 'temporary')
    readonly RESERVED_FILE_NAMES_PATTERN = this.RESERVED_FOLDER_NAMES_PATTERN.filter(
    name => name.toLowerCase() !== 'permanent' && name.toLowerCase() !== 'temporary'
    );
    readonly RESERVED_FILE_NAMES_PATTERN_TEST = this.RESERVED_FILE_NAMES_PATTERN;
    emailExtensions = ['eml', 'msg', 'txt'];

    getImageForFile(fileName: string): string {
        const extension = this.getFileExtension(fileName).toLowerCase();

        // Check if the result is cached
        if (this.fileImageCache[extension]) {
            return this.fileImageCache[extension];
        }
        
        let imagePath: string;

        switch (extension) {
            case 'txt':
                imagePath = 'assets/img/dms-icons/file-text.svg';
                break;
            case 'jpeg':
            case 'jpg':
            case 'png':
                imagePath = 'assets/img/dms-icons/file-image.svg';
                break;
            case 'pdf':
                imagePath = 'assets/img/dms-icons/file-pdf.svg';
                break;
            case 'xlsx':
                imagePath = 'assets/img/dms-icons/file-xlsx.svg';
                break;
            case 'xls':
                imagePath = 'assets/img/dms-icons/file-excel.svg';
                break;
            case 'docx':
            case 'doc':
                imagePath = 'assets/img/dms-icons/file-word.svg';
                break;
            case 'pptx':
                imagePath = 'assets/img/dms-icons/file-pptx.svg';
                break;
            case 'vsdx':
                imagePath = 'assets/img/dms-icons/file-vsdx.svg';
                break;
            case 'eml':
            case 'msg':            
               imagePath = 'assets/img/dms-icons/email.svg';
                break;
            case 'webm':
            case 'mp3':
            case 'mp4':
            case 'mov':
            case 'wmv':
            case 'avi':
            case 'mkv':
            case 'ts':
                imagePath = 'assets/img/dms-icons/file-video.svg';
                break;
            case '':
                imagePath = 'assets/img/dms-icons/folder.svg';
                break;
            default:
                imagePath = 'assets/img/dms-icons/file-default.svg';
                break;
        }

        // Cache the result for future lookups
        this.fileImageCache[extension] = imagePath;

        return imagePath;
    }


    getFileExtension(fileName: string): string {
        const parts = fileName.split('.');
        return parts.length > 1 ? parts.pop() : '';
    }

    getExtension(fileName: string): string {
        const parts = fileName.split('.');
        return parts.length > 1 ? parts.pop()?.toLowerCase() || '' : '';
    }
    extensionToDocNameMap: { [key: string]: { docName: string, defaultFormat: number } } = {
        'msg': { docName: 'Microsoft Message File', defaultFormat: DocDefaultPreferenceEnum.Preview },
        'eml': { docName: 'Email Message', defaultFormat: DocDefaultPreferenceEnum.Preview },
        'xml': { docName: 'xml', defaultFormat: DocDefaultPreferenceEnum.Preview },
        'txt': { docName: 'txt', defaultFormat: DocDefaultPreferenceEnum.Preview },
        // add more mappings as needed
        'other': { docName: 'Other', defaultFormat: DocDefaultPreferenceEnum.Browser }
      };

    fileNameValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            const value = control.value;

            if (value !== null && typeof value === 'string') {
                if (value.trim() === '') {
                    return { emptyString: true };
                }

                if (this.FORBIDDEN_FILENAME_CHARS_REGEX_TEST.test(value)) {
                    return { invalidCharacters: true };
                }
            }

            return null;
        };
    }

    reservedFileNameValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            const value = control.value;

            if (value !== null && typeof value === 'string') {
                if (value.trim() === '') {
                    return { emptyString: true };
                }

                // Check if value is in reserved names (case-insensitive)
                if (this.RESERVED_FILE_NAMES_PATTERN_TEST.some(name => name.toLowerCase() === value.toLowerCase())) {
                    return { reservedFileName: { value: control.value } };
                }
            }

            return null;
        };
    }

    reservedFolderNameValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const rawValue = control.value;

            // Ensure value is a string and trimmed
            const value = typeof rawValue === 'string' ? rawValue.trim().toLowerCase() : '';

            if (!value) {
                return null;
            }

            // Check if value is in reserved names (case-insensitive)
            if (this.RESERVED_FOLDER_NAMES_PATTERN.some(name => name.toLowerCase() === value.toLowerCase())) {
                return { reservedFolderName: { value: control.value } };
            }
            
            return null;
        };
    }

    sanitizeFileName(name: string): string {
        const trimmed = name.trim();
        const extensionIndex = trimmed.lastIndexOf('.');
        const baseName = extensionIndex >= 0 ? trimmed.slice(0, extensionIndex) : trimmed;
        const extension = extensionIndex >= 0 ? trimmed.slice(extensionIndex) : '';

        // Remove forbidden characters        
        const cleanedBase = baseName.replace(this.FORBIDDEN_FILENAME_CHARS_REGEX, '_');

        // Collapse multiple underscores and trim again
        const normalizedBase = cleanedBase.replace(/[_]+/g, '_').trim();

        return `${normalizedBase}${extension}`;
    }

    getShortBreadcrumb(path: string, limit = 3): string {
        if (!path) return '';
        const segments = path.split(' / ');
        if (segments.length <= limit) return path;
        return `... / ${segments.slice(-limit).join(' / ')}`;
    }
}